/*   
    Developers: Sakshi Bhise     
    Script: models.ts     
    Date: 06/18/2025    
    Project Name: AskHR     
    Team: DSET     
  
    Purpose of the Script:   
    This script serves as a centralized location for defining all data models and type definitions used in the AskHR application backend and frontend interactions.   
    It provides type safety, structural consistency, and code maintainability across various components, including chat conversations, responses, citations, user information, application settings, error messages, and feedback mechanisms.  
    By consolidating these model definitions in one script, developers can ensure uniform representation and facilitate easier code updates, validations, and integrations with external systems.  
*/  
  
// Defines a single citation source for an answer.  
export type Citation = {      
    content: string;                     // The content or snippet of the citation.  
    id: string;                          // Unique identifier for the citation.  
    title: string | null;                // Title of the source, if available.  
    filepath: string | null;             // File path for internal references.  
    url: string | null;                  // Associated external URL, if any.  
    metadata: string | null;             // Additional metadata for the citation.  
    chunk_id: string | null;             // Identifier for a chunk within the document.  
    reindex_id: string | null;           // Reindex identifier, if applicable.  
}  
  
// Represents the main response object for an AskHR query.  
export type AskResponse = {      
    answer: string;                      // The main answer or response text.  
    citations: Citation[];               // Array of citations supporting the answer.  
    error?: string;                      // Optional error message.  
    message_id?: string;                 // Optional ID for the message.  
    feedback?: Feedback;                 // Optional feedback on the response.  
    followUpQuestions: string[]          // Follow up questions on answer
    date?: string;                       // ISO timestamp of when the message was sent — optional, used to display a timestamp on the message bubble
}  
  
// Structure for content communicated by tool messages.  
export type ToolMessageContent = {      
    citations: Citation[];               // Citations related to the message intent.  
    intent: string;                      // Intent type or description.  
}  
  
// Represents an individual message in a chat conversation.  
export type ChatMessage = {      
    id: string;                          // Unique message identifier.  
    role: string;                        // Role of the sender (e.g., user, assistant).  
    content: string;                     // Message text.  
    end_turn?: boolean;                  // Optional flag for end of chat turn.  
    date: string;                        // Timestamp for the message.  
    feedback?: Feedback;                 // Optional user feedback attached.  
    context?: string;                    // Additional context, if any.  
}  
  
// Contains metadata and messages for an entire conversation.  
export type Conversation = {      
    id: string;                          // Conversation unique identifier.  
    title: string;                       // Title or topic of the conversation.  
    messages: ChatMessage[];             // Array of chat messages.  
    date: string;                        // Date the conversation took place.  
}  
  
// Enum for types of chat completions the backend may return.  
export enum ChatCompletionType {      
    ChatCompletion = "chat.completion",  
    ChatCompletionChunk = "chat.completion.chunk"  
}  
  
// Wrapper object for chat response choices.  
export type ChatResponseChoice = {      
    messages: ChatMessage[];             // List of messages in this choice.  
}  
  
// Standard structure for chat completion responses.  
export type ChatResponse = {      
    id: string;                          // Response unique identifier.  
    model: string;                       // Model name or version used.  
    created: number;                     // Timestamp of creation.  
    object: ChatCompletionType;          // Type of chat completion object.  
    choices: ChatResponseChoice[];       // Array of response choices.  
    history_metadata: {                  // Metadata on the conversation history.  
        conversation_id: string;  
        title: string;  
        date: string;  
    }  
    error?: any;                         // Optional error metadata.  
    completion_tokens?: string;          // Total tokens used in completion.  
    prompt_tokens?: string;              // Total prompt tokens used.  
}  
  
// Request structure for initiating or continuing a conversation.  
export type ConversationRequest = {      
    messages: ChatMessage[];             // Incoming messages to process.  
    user_permission?: any;               // Optional, for RBAC/extensions.  
}  
  
// Information about the authenticated user.  
export type UserInfo = {      
    access_token: string;                // User access token.  
    expires_on: string;                  // Expiry date/time for the token.  
    id_token: string;                    // ID token for the user session.  
    provider_name: string;               // Name of the authentication provider.  
    user_claims: any[];                  // List of user claims.  
    user_id: string;                     // Unique user identifier.  
}  
  
// Status enums for tracking SQL DB connectivity.  
export enum SQLDBStatus {      
    NotConfigured = "Sql DB is not configured",  
    NotWorking = "Sql DB is not working",  
    InvalidCredentials = "Sql DB has invalid credentials",  
    InvalidDatabase = "Invalid Sql DB database name",  
    InvalidContainer = "Invalid Sql DB container name",  
    Working = "Sql DB is configured and working",  
}  
  
// Structure to check SQL DB connectivity and health.  
export type SQLDBHealth = {      
    sqlDB: boolean,  
    status: string  
}  
  
// Enum for tracking chat history loading state for UIs.  
export enum ChatHistoryLoadingState {      
    Loading = "loading",               
    Success = "success",               
    Fail = "fail",                     
    NotStarted = "notStarted"  
}  
  
// Standard error message structure for UI/feedback.  
export type ErrorMessage = {      
    title: string,  
    subtitle: string  
}  
  
// UI customization structure to adjust appearance and features.  
export type UI = {      
    title: string;                    // Main app or page title.  
    chat_title: string;               // Title for the chat window section.  
    chat_description: string;         // Description or subtitle for chat section.  
    logo?: string;                    // Logo image (optional).  
    env: string;                      // env type
    chat_logo?: string;               // Chat window logo (optional).  
    show_share_button?: boolean;      // Optionally show/hide share button.  
}  
  
// Settings for general frontend behavior and feature toggles.  
export type FrontendSettings = {      
    auth_enabled?: string | null;         // Auth enable toggle.  
    feedback_enabled?: string | null;     // Feedback enable toggle.  
    ui?: UI;                              // UI customization options.  
    consent_interval?: string;
}  
  
// Enum for classifying various types of user feedback.  
export enum Feedback {      
    Neutral = "neutral",  
    Positive = "positive",  
    Negative = "negative",  
    MissingCitation = "no_information_found",  
    WrongCitation = "missing_citation_or_wrong_citation",  
    OutOfScope = "out_of_scope",  
    InaccurateOrIrrelevant = "inaccurate_irrelevant_incomplete",  
    OtherUnhelpful = "other_unhelpful",  
    HateSpeech = "hate_speech",  
    Violent = "violent",  
    Sexual = "sexual",  
    Manipulative = "manipulative",  
    OtherHarmful = "other_harmlful",
    Report = "report"  
}  
// ==============================================================================
// User
// ==============================================================================
/**
 * User identity extracted for User Name.
 */
export type User = {
    firstName: string;
    lastName: string;
};
  